import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { Router } from '@angular/router';

import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';

import { setSelectedLanguage } from 'src/app/dashboard/state/study/study.actions';
import {
  createInformativePage,
  updateInformativePage,
} from 'src/app/dashboard/state/informative-page/informative-page.actions';
import {
  selectSelectedLanguage,
  selectSelectedStudy,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/study/study.selectors';
import { selectSelectedInformativePage } from 'src/app/dashboard/state/informative-page/informative-page.selectors';
import { AppState } from 'src/app/state';
import { Language } from 'src/app/models/language';
import { Study } from 'src/app/models/study';
import { StudyType } from 'src/app/models/study-type';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { InformativePage } from 'src/app/models/informative-page';
import { STUDY_TITLE_IDENTIFIER } from 'src/app/dashboard/studies/pages/study-details/study-details.component';
import { STUDIES_ROUTES } from 'src/app/dashboard/studies/studies.routes';
import { removeWhitespaces } from 'src/app/core/custom-form-validators';
import { isThereEmptyTranslation } from '../../../helpers/translation-helper';
import { tinymceInitConfig } from '../../../../../shared/components/tinymce-editor/tinymce-editor-config';
import '../../../../../../assets/js/tinymce-cc-image.plugin';

const INFORMATIVE_PAGE_CONTENT_IDENTIFIER = 'content';

@Component({
  selector: 'dashboard-informative-page',
  templateUrl: './informative-page.component.html',
  styleUrls: ['./informative-page.component.scss'],
})
export class InformativePageComponent implements OnInit, OnDestroy {
  @Input() supportedLanguages: Language[];
  @Input() selectedLanguage: Language;
  @Output() valueChange = new EventEmitter();

  @ViewChild('languageTabs', { static: false }) languageTabs: MatTabGroup;
  @ViewChild('languageTabsEditor', { static: false })
  languageTabsEditor: MatTabGroup;

  subscriptionPageForm: FormGroup;

  selectedInformativePage$: Observable<InformativePage>;
  selectedInformativePage: InformativePage;

  selectedStudy: Study;
  StudyType = StudyType;

  isEdit: boolean;
  isImageToEdit: boolean;
  imgPath: string;
  idsForUpload: UploadIds;
  keyForUpload: string;

  formSubmitted = false;

  tinymceInitConfig = {
    ...tinymceInitConfig,
    height: 380,
  };

  private subscriptions = new Subscription();

  constructor(
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private router: Router,
    private toastrService: ToastrService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptionPageForm = this.formBuilder.group({
      translations: this.formBuilder.array([]),
      isThanks: [true],
      isEmail: [{ value: null, disabled: true }],
      imagePath: [null],
    });

    this.keyForUpload = this.router.url.endsWith(`/${STUDIES_ROUTES.SUBSCRIPTION}`)
      ? UploadKeys.UPLOAD_FROM_SUBSCRIPTION
      : UploadKeys.UPLOAD_FROM_INFORMATIVE_PAGE;

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => {
        this.selectedLanguage = language;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.selectedInformativePage$ = this.store.select(selectSelectedInformativePage);
    this.subscriptions.add(
      this.selectedInformativePage$.subscribe((informativePage) => {
        if (informativePage) {
          this.selectedInformativePage = informativePage;
          this.isEdit = true;
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((selectedStudy) => {
        this.selectedStudy = selectedStudy;
        if (this.selectedStudy?.type === StudyType.CC) {
          this.tinymceInitConfig = {
            ...this.tinymceInitConfig,
            plugins: tinymceInitConfig.plugins.concat(' ccimagepicker'),
            toolbar: tinymceInitConfig.toolbar.concat(' ccImagePicker'),
          };
        }
      })
    );

    if (this.isEdit) {
      this.removeAllTranslationFields();
      if (this.selectedInformativePage?.imagePath) {
        this.isImageToEdit = true;
        this.imgPath = this.selectedInformativePage?.imagePath;
        this.idsForUpload = {
          studyId: this.selectedStudy?.id,
        };
      } else {
        this.idsForUpload = {
          studyId: this.selectedStudy?.id,
        };
      }
      if (this.supportedLanguages) {
        this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
      }
    } else {
      this.removeAllTranslationFields();
      if (this.supportedLanguages) {
        this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
      } else {
        this.createTranslationFields(this.selectedLanguage.code);
      }
    }
  }

  get translations(): FormArray {
    return this.subscriptionPageForm.get('translations') as FormArray;
  }

  get isThanks(): AbstractControl {
    return this.subscriptionPageForm.get('isThanks');
  }

  get isEmail(): AbstractControl {
    return this.subscriptionPageForm.get('isEmail');
  }

  get imagePath(): AbstractControl {
    return this.subscriptionPageForm.get('imagePath');
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedInformativePage?.translations?.filter(
      (translation) => translation?.language === languageCode
    );
    const subscriptionPageTitle = this.formBuilder.group({
      id: [filteredTranslations ? filteredTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [STUDY_TITLE_IDENTIFIER],
      translation: [
        filteredTranslations ? filteredTranslations[0]?.translation : null,
        [Validators.required, removeWhitespaces],
      ],
    });

    const subscriptionPageContent = this.formBuilder.group({
      id: [filteredTranslations ? filteredTranslations[1]?.id : 0],
      language: [languageCode],
      identifier: [INFORMATIVE_PAGE_CONTENT_IDENTIFIER],
      translation: [
        filteredTranslations ? filteredTranslations[1]?.translation : '',
        [Validators.required, removeWhitespaces],
      ],
    });

    this.translations.push(subscriptionPageTitle);
    this.translations.push(subscriptionPageContent);
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language?.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  selectFirstTab() {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  savePage(): void {
    this.formSubmitted = true;

    if (isThereEmptyTranslation(this.translations.value)) {
      this.toastrService.error('Please populate Title and Content in all selected languages');
      return;
    }

    if (this.subscriptionPageForm.invalid) {
      return;
    }

    if (this.selectedStudy?.type !== StudyType.TIIM) {
      this.isThanks.setValue(false);
    }

    let pageDetails = {
      ...this.subscriptionPageForm.value,
      isThanks: +this.isThanks.value,
      studyId: this.selectedStudy?.id,
    };

    if (this.isEdit) {
      pageDetails = {
        ...pageDetails,
        pageId: this.selectedInformativePage?.id,
      };
      this.store.dispatch(updateInformativePage({ informativePage: pageDetails }));
      this.valueChange.emit();
      return;
    }
    this.store.dispatch(createInformativePage({ informativePage: pageDetails }));
    this.valueChange.emit();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
